import { useUser } from '../hooks/useUser';
import { useGamePositionPerformance } from '../hooks/useGamePositionPerformance';

export function GamePositionAnalysis() {
  const { selectedPlayer } = useUser();
  const { gameData, loading, error } = useGamePositionPerformance(selectedPlayer?.bowler_id || null);

  console.log('GamePositionAnalysis render:', { 
    selectedPlayer: selectedPlayer?.bowler_name, 
    bowlerId: selectedPlayer?.bowler_id,
    gameDataLength: gameData.length,
    gameData,
    loading,
    error: error?.message
  });

  if (!selectedPlayer) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Game Position Analysis
          </h1>
          <p className="text-gray-600">
            Performance by game position (1st, 2nd, 3rd game)
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            No Player Selected
          </h2>
          
          <p className="text-gray-600">
            Please select a player to view their game position performance analysis.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Game Position Analysis
          </h1>
          <p className="text-gray-600">
            Performance by game position for {selectedPlayer.bowler_name}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-8">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Game Position Analysis
          </h1>
          <p className="text-gray-600">
            Performance by game position for {selectedPlayer.bowler_name}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Error Loading Data
          </h2>
          
          <p className="text-gray-600 mb-4">
            {error.message}
          </p>
          
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (gameData.length === 0) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Game Position Analysis
          </h1>
          <p className="text-gray-600">
            Performance by game position for {selectedPlayer.bowler_name}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            No Game Position Data Available
          </h2>
          
          <p className="text-gray-600">
            {selectedPlayer.bowler_name} hasn't bowled enough games this season, or game position data is not available.
          </p>
        </div>
      </div>
    );
  }

  // Calculate trend analysis
  const calculateTrend = () => {
    if (gameData.length < 2) return 'insufficient-data';
    
    const game1 = gameData.find(g => g.game_position === 1);
    const game3 = gameData.find(g => g.game_position === 3);
    
    if (!game1 || !game3) return 'insufficient-data';
    
    const difference = game3.player_average - game1.player_average;
    
    if (Math.abs(difference) < 2) return 'consistent';
    return difference > 0 ? 'improving' : 'declining';
  };

  const trend = calculateTrend();
  const bestGame = gameData.reduce((best, current) => 
    current.player_average > best.player_average ? current : best
  );
  const worstGame = gameData.reduce((worst, current) => 
    current.player_average < worst.player_average ? current : worst
  );

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return (
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      case 'declining':
        return (
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
          </svg>
        );
      case 'consistent':
        return (
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getTrendText = (trend: string) => {
    switch (trend) {
      case 'improving':
        return 'Improving';
      case 'declining':
        return 'Declining';
      case 'consistent':
        return 'Consistent';
      default:
        return 'Unknown';
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'improving':
        return 'text-green-600';
      case 'declining':
        return 'text-red-600';
      case 'consistent':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Game Position Analysis
        </h1>
        <p className="text-gray-600">
          Performance by game position for {selectedPlayer.bowler_name}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Best Game</p>
              <p className="text-2xl font-bold text-gray-900">Game {bestGame.game_position}</p>
              <p className="text-sm text-green-600">{bestGame.player_average.toFixed(1)} avg</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Challenging Game</p>
              <p className="text-2xl font-bold text-gray-900">Game {worstGame.game_position}</p>
              <p className="text-sm text-red-600">{worstGame.player_average.toFixed(1)} avg</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              {getTrendIcon(trend)}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Series Trend</p>
              <p className="text-2xl font-bold text-gray-900">{getTrendText(trend)}</p>
              <p className={`text-sm ${getTrendColor(trend)}`}>Across games</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Games</p>
              <p className="text-2xl font-bold text-gray-900">
                {gameData.reduce((sum, game) => sum + game.games_played, 0)}
              </p>
              <p className="text-sm text-gray-500">This season</p>
            </div>
          </div>
        </div>
      </div>

      {/* Game Position Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
        {gameData.map((game) => {
          const isAboveAverage = game.performance_difference > 0;
          const isSignificant = Math.abs(game.performance_difference) > 3;
          
          return (
            <div key={game.game_position} className="bg-white rounded-lg shadow p-4 sm:p-6 touch-manipulation">
              <div className="text-center mb-4">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${
                  game.game_position === 1 ? 'bg-blue-100' :
                  game.game_position === 2 ? 'bg-yellow-100' : 'bg-green-100'
                }`}>
                  <span className={`text-xl sm:text-2xl font-bold ${
                    game.game_position === 1 ? 'text-blue-600' :
                    game.game_position === 2 ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {game.game_position}
                  </span>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Game {game.game_position}
                </h3>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-gray-600">Your Average</span>
                  <span className="text-base sm:text-lg font-semibold text-gray-900">
                    {game.player_average.toFixed(1)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-gray-600">League Average</span>
                  <span className="text-xs sm:text-sm text-gray-500">
                    {game.league_average.toFixed(1)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-gray-600">Difference</span>
                  <span className={`text-xs sm:text-sm font-medium ${
                    isAboveAverage ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {isAboveAverage ? '+' : ''}{game.performance_difference.toFixed(1)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-gray-600">Games Played</span>
                  <span className="text-xs sm:text-sm text-gray-500">
                    {game.games_played}
                  </span>
                </div>

                <div className="pt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Performance</span>
                    <span className={`text-xs font-medium ${
                      isSignificant 
                        ? isAboveAverage ? 'text-green-700' : 'text-red-700'
                        : 'text-gray-500'
                    }`}>
                      {isAboveAverage ? 'Above Avg' : 'Below Avg'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                    <div 
                      className={`h-2 sm:h-3 rounded-full ${
                        isAboveAverage ? 'bg-green-500' : 'bg-red-500'
                      }`}
                      style={{ 
                        width: `${Math.min(Math.abs(game.performance_difference) * 3, 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed Analysis */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Detailed Game Position Analysis</h2>
          <p className="text-sm text-gray-600">Performance breakdown and trends across game positions</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Performance Insights */}
            <div>
              <h3 className="text-base sm:text-md font-semibold text-gray-900 mb-4">Performance Insights</h3>
              <div className="space-y-3 sm:space-y-4">
                {trend === 'improving' && (
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Positive Series Trend</p>
                      <p className="text-xs sm:text-sm text-gray-600">You tend to improve as the series progresses, showing good adaptability and focus.</p>
                    </div>
                  </div>
                )}
                
                {trend === 'declining' && (
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Declining Series Trend</p>
                      <p className="text-xs sm:text-sm text-gray-600">Performance tends to decrease as the series progresses. Consider fatigue management or mental focus strategies.</p>
                    </div>
                  </div>
                )}
                
                {trend === 'consistent' && (
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Consistent Performance</p>
                      <p className="text-xs sm:text-sm text-gray-600">You maintain steady performance throughout the series, showing good consistency and mental stability.</p>
                    </div>
                  </div>
                )}

                {gameData.some(g => g.performance_difference > 5) && (
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Strong Game Position</p>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Game {gameData.find(g => g.performance_difference > 5)?.game_position} is significantly above league average.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Recommendations */}
            <div>
              <h3 className="text-base sm:text-md font-semibold text-gray-900 mb-4">Recommendations</h3>
              <div className="space-y-3 sm:space-y-4">
                {trend === 'declining' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-yellow-800">
                      <strong>Focus on Endurance:</strong> Consider working on physical and mental endurance to maintain performance throughout the series.
                    </p>
                  </div>
                )}
                
                {trend === 'improving' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-green-800">
                      <strong>Leverage Your Strength:</strong> Your ability to improve during series is valuable. Use early games for reading lane conditions.
                    </p>
                  </div>
                )}

                {worstGame.performance_difference < -5 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-red-800">
                      <strong>Game {worstGame.game_position} Focus:</strong> This game position needs attention. Consider specific practice or strategy adjustments.
                    </p>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                  <p className="text-xs sm:text-sm text-blue-800">
                    <strong>General Tip:</strong> Track your game-by-game performance to identify patterns and make strategic adjustments.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}